import React, { useEffect, useState } from "react";
import AuthUser from "./AuthUser";
import toast, { Toaster } from "react-hot-toast";
import moment from "moment";
import TaskSkeleton from "./TaskSkeleton";
import XLSX from "xlsx-js-style";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const tableStyles = `
    .student-summary-table { font-size: 0.85rem; width: 100%; }
    .student-summary-table thead th { font-size: 0.8rem; padding: 10px 14px; white-space: nowrap; font-weight: 600; text-align: center; vertical-align: middle; background: #1565C0; color: #fff; }
    .student-summary-table tbody td { font-size: 0.83rem; padding: 9px 14px; text-align: center; vertical-align: middle; }
    .student-summary-table tbody td:first-child { text-align: left; font-weight: 500; }
    .class-group td { background: #e3f2fd !important; font-weight: 700; }
    .grand-total-row td { background: #ffecb3 !important; font-weight: 700; font-size: 0.85rem !important; }
`;

export default function StudentSummary() {
    const { http, user } = AuthUser();
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [stYear, setStYear] = useState([]);
    const [selectedYear, setSelectedYear] = useState("");
    const [showDivision, setShowDivision] = useState(true);

    useEffect(() => {
        http.get(`/year`).then((res) => setStYear(res.data.data));
        fetchData();
    }, []);

    const fetchData = (year = "") => {
        setIsLoading(true);
        http.get(`/student_summary?company_id=${user.company_id}&year=${year}`)
            .then((res) => { setIsLoading(false); setData(res.data.data || []); })
            .catch(() => { setIsLoading(false); toast.error("Error fetching data"); });
    };

    const handleYearChange = (e) => {
        setSelectedYear(e.target.value);
        fetchData(e.target.value);
    };

    const grandTotal = data.reduce((a, r) => ({
        newCount: a.newCount + r.new_count, oldCount: a.oldCount + r.old_count, total: a.total + r.total,
    }), { newCount: 0, oldCount: 0, total: 0 });

    const classTotals = {};
    data.forEach((r) => {
        if (!classTotals[r.class]) classTotals[r.class] = { newCount: 0, oldCount: 0, total: 0 };
        classTotals[r.class].newCount += r.new_count;
        classTotals[r.class].oldCount += r.old_count;
        classTotals[r.class].total += r.total;
    });

    const classOrder = [...new Set(data.map((r) => r.class))];

    const getExportData = () => {
        const headers = showDivision ? ["Class", "Division", "New", "Old", "Total"] : ["Class", "New", "Old", "Total"];
        const rows = [];

        if (showDivision) {
            classOrder.forEach((cls) => {
                const classRows = data.filter((r) => r.class === cls);
                classRows.forEach((r) => rows.push({ data: [r.class, r.division, r.new_count, r.old_count, r.total], style: null }));
                const ct = classTotals[cls];
                if (classRows.length > 1) rows.push({ data: [cls + " Total", "", ct.newCount, ct.oldCount, ct.total], style: "class" });
            });
            rows.push({ data: ["Grand Total", "", grandTotal.newCount, grandTotal.oldCount, grandTotal.total], style: "grand" });
        } else {
            classOrder.forEach((cls) => {
                const ct = classTotals[cls];
                rows.push({ data: [cls, ct.newCount, ct.oldCount, ct.total], style: null });
            });
            rows.push({ data: ["Grand Total", grandTotal.newCount, grandTotal.oldCount, grandTotal.total], style: "grand" });
        }
        return { headers, rows };
    };

    const handleExportExcel = () => {
        if (data.length === 0) { toast.error("No data"); return; }
        const { headers, rows } = getExportData();
        const colCount = headers.length;
        const numStart = showDivision ? 2 : 1;

        const headerStyle = { font: { bold: true, color: { rgb: "FFFFFF" }, sz: 10 }, fill: { fgColor: { rgb: "1565C0" } }, alignment: { horizontal: "center" }, border: { bottom: { style: "thin", color: { rgb: "000000" } } } };
        const classStyle = { font: { bold: true, sz: 10 }, fill: { fgColor: { rgb: "BBDEFB" } }, alignment: { horizontal: "center" } };
        const grandStyle = { font: { bold: true, sz: 11 }, fill: { fgColor: { rgb: "FFECB3" } }, alignment: { horizontal: "center" } };
        const centerAlign = { alignment: { horizontal: "center" } };
        const numFmt = { numFmt: "#,##0" };

        const wsData = [headers, ...rows.map((r) => r.data)];
        const ws = XLSX.utils.aoa_to_sheet(wsData);
        headers.forEach((_, ci) => { const ref = XLSX.utils.encode_cell({ r: 0, c: ci }); if (ws[ref]) ws[ref].s = headerStyle; });
        rows.forEach((row, ri) => {
            const er = ri + 1;
            const sty = row.style === "class" ? classStyle : row.style === "grand" ? grandStyle : centerAlign;
            for (let ci = 0; ci < colCount; ci++) {
                const ref = XLSX.utils.encode_cell({ r: er, c: ci });
                if (ws[ref]) ws[ref].s = { ...(ws[ref].s || {}), ...sty };
            }
            for (let ci = numStart; ci < colCount; ci++) {
                const ref = XLSX.utils.encode_cell({ r: er, c: ci });
                if (ws[ref] && typeof ws[ref].v === "number") ws[ref].s = { ...(ws[ref].s || {}), ...numFmt };
            }
        });
        const cols = showDivision
            ? [{ wch: 20 }, { wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 10 }]
            : [{ wch: 20 }, { wch: 10 }, { wch: 10 }, { wch: 10 }];
        ws["!cols"] = cols;
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Student Summary");
        XLSX.writeFile(wb, `Student_Summary_${moment().format("YYYYMMDD_HHmm")}.xlsx`);
    };

    const handleExportPdf = () => {
        if (data.length === 0) { toast.error("No data"); return; }
        const { headers, rows } = getExportData();
        const numStart = showDivision ? 2 : 1;
        const colStyles = {};
        for (let i = numStart; i < headers.length; i++) colStyles[i] = { halign: "center" };

        const doc = new jsPDF("p", "mm", "a4");
        doc.setFontSize(14); doc.text("Student Summary", 14, 15);
        doc.setFontSize(9); doc.text(`Year: ${selectedYear || "All"} | Date: ${moment().format("DD MMM YYYY")}`, 14, 22);

        autoTable(doc, {
            head: [headers], body: rows.map((r) => r.data), startY: 28,
            styles: { fontSize: 9, cellPadding: 3, halign: "center" },
            headStyles: { fillColor: [21, 101, 192], textColor: 255, fontStyle: "bold", halign: "center" },
            columnStyles: colStyles,
            didParseCell: (cellData) => {
                if (cellData.section !== "body") return;
                const row = rows[cellData.row.index];
                if (!row) return;
                if (row.style === "class") { cellData.cell.styles.fillColor = [187, 222, 251]; cellData.cell.styles.fontStyle = "bold"; }
                else if (row.style === "grand") { cellData.cell.styles.fillColor = [255, 236, 179]; cellData.cell.styles.fontStyle = "bold"; }
            },
        });
        doc.save(`Student_Summary_${moment().format("YYYYMMDD_HHmm")}.pdf`);
    };

    const handlePrint = () => {
        const printContent = document.getElementById("studentSummaryTable");
        if (!printContent) return;
        const win = window.open("", "_blank");
        win.document.write(`<html><head><title>Student Summary</title>
            <style>body{font-family:Arial,sans-serif;padding:20px}table{width:100%;border-collapse:collapse;font-size:13px}th,td{border:1px solid #ccc;padding:8px 12px;text-align:center}th{background:#1565C0;color:#fff}.class-group td{background:#e3f2fd;font-weight:700}.grand-total-row td{background:#ffecb3;font-weight:700}h3{margin-bottom:5px}small{color:#666}</style>
            </head><body><h3>Student Summary</h3><small>Year: ${selectedYear || "All"} | Printed: ${moment().format("DD MMM YYYY")}</small>${printContent.outerHTML}</body></html>`);
        win.document.close();
        win.print();
    };

    return (
        <>
            <style>{tableStyles}</style>
            <Toaster />
            <div className="container-fluid mt-2 px-4">
                <div className="d-flex align-items-center justify-content-between mb-2">
                    <h5 className="mb-0 fw-bold">Student Summary</h5>
                    <div className="d-flex gap-1 flex-wrap align-items-center">
                        <div className="form-check form-switch me-2" style={{ fontSize: "0.78rem" }}>
                            <input className="form-check-input" type="checkbox" id="divToggle" checked={showDivision} onChange={(e) => setShowDivision(e.target.checked)} />
                            <label className="form-check-label" htmlFor="divToggle">Division</label>
                        </div>
                        <button className="btn btn-sm btn-success px-2 py-0" style={{ fontSize: "0.75rem" }} onClick={handleExportExcel} disabled={data.length === 0}><i className="fas fa-file-excel me-1"></i>Excel</button>
                        <button className="btn btn-sm btn-danger px-2 py-0" style={{ fontSize: "0.75rem" }} onClick={handleExportPdf} disabled={data.length === 0}><i className="fas fa-file-pdf me-1"></i>PDF</button>
                        <button className="btn btn-sm btn-info text-white px-2 py-0" style={{ fontSize: "0.75rem" }} onClick={handlePrint} disabled={data.length === 0}><i className="fas fa-print me-1"></i>Print</button>
                    </div>
                </div>

                <div className="row g-2 align-items-center mb-2">
                    <div className="col-md-2">
                        <select onChange={handleYearChange} className="form-control form-control-sm border border-danger">
                            <option value="">All Year</option>
                            {stYear.map((o, i) => <option key={i} value={o.name}>{o.name}</option>)}
                        </select>
                    </div>
                    <div className="col-auto">
                        <small className="text-muted">Total: <strong>{grandTotal.total}</strong> (New: <strong className="text-success">{grandTotal.newCount}</strong>, Old: <strong className="text-primary">{grandTotal.oldCount}</strong>)</small>
                    </div>
                </div>

                {isLoading ? (
                    <TaskSkeleton />
                ) : data.length === 0 ? (
                    <div className="alert alert-warning mt-2">No student data found.</div>
                ) : (
                    <div className="table-responsive">
                        <table id="studentSummaryTable" className="table table-bordered table-hover student-summary-table mt-1">
                            <thead>
                                <tr>
                                    <th>Class</th>
                                    {showDivision && <th>Division</th>}
                                    <th>New</th>
                                    <th>Old</th>
                                    <th>Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {showDivision ? (
                                    classOrder.map((cls) => {
                                        const classRows = data.filter((r) => r.class === cls);
                                        const ct = classTotals[cls];
                                        return (
                                            <React.Fragment key={cls}>
                                                {classRows.map((r, idx) => (
                                                    <tr key={`${cls}-${idx}`}>
                                                        <td>{idx === 0 ? <strong>{r.class}</strong> : ""}</td>
                                                        <td>{r.division}</td>
                                                        <td>{r.new_count > 0 ? r.new_count : "-"}</td>
                                                        <td>{r.old_count > 0 ? r.old_count : "-"}</td>
                                                        <td><strong>{r.total}</strong></td>
                                                    </tr>
                                                ))}
                                                {classRows.length > 1 && (
                                                    <tr className="class-group">
                                                        <td><strong>{cls} Total</strong></td>
                                                        <td></td>
                                                        <td><strong>{ct.newCount}</strong></td>
                                                        <td><strong>{ct.oldCount}</strong></td>
                                                        <td><strong>{ct.total}</strong></td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })
                                ) : (
                                    classOrder.map((cls) => {
                                        const ct = classTotals[cls];
                                        return (
                                            <tr key={cls}>
                                                <td><strong>{cls}</strong></td>
                                                <td>{ct.newCount > 0 ? ct.newCount : "-"}</td>
                                                <td>{ct.oldCount > 0 ? ct.oldCount : "-"}</td>
                                                <td><strong>{ct.total}</strong></td>
                                            </tr>
                                        );
                                    })
                                )}
                                <tr className="grand-total-row">
                                    <td><strong>Grand Total</strong></td>
                                    {showDivision && <td></td>}
                                    <td><strong>{grandTotal.newCount}</strong></td>
                                    <td><strong>{grandTotal.oldCount}</strong></td>
                                    <td><strong>{grandTotal.total}</strong></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </>
    );
}
